import type { TiptapDoc } from '@odo/shared-ui';

export type Run = string | { bold: string };
/** One visual line of runs. */
export type Line = Run[];
/** One paragraph; its lines are joined by hard breaks. */
export type Block = Line[];

interface TextNode {
    type: 'text';
    text: string;
    marks?: { type: 'bold' }[];
}
type InlineNode = TextNode | { type: 'hardBreak' };
interface ParagraphNode {
    type: 'paragraph';
    content?: InlineNode[];
}
interface ListItemNode {
    type: 'listItem';
    content: ParagraphNode[];
}
interface BulletListNode {
    type: 'bulletList';
    content: ListItemNode[];
}
export type BlockNode = ParagraphNode | BulletListNode;

// Ends a line on a plain run after a bold label, so the caret placed at the line's end (and
// Enter from there) types unbolded text; the plain-text projection trims it back off.
export const PLAIN_TAIL = ' ';

function textNode(run: Run): TextNode {
    return typeof run === 'string'
        ? { type: 'text', text: run }
        : { type: 'text', text: run.bold, marks: [{ type: 'bold' }] };
}

export function paragraph(lines: Block): ParagraphNode {
    const content = lines.flatMap((line, i): InlineNode[] => [
        ...(i > 0 ? [{ type: 'hardBreak' as const }] : []),
        ...line.filter((run) => run !== '').map(textNode)
    ]);
    return { type: 'paragraph', content };
}

// The editor packs paragraphs tight, so an empty one gives the blank line of the original;
// the plain-text projection drops it and joins blocks with a blank line anyway.
export function blankLine(): ParagraphNode {
    return { type: 'paragraph' };
}

export function bulletList(items: Line[]): BulletListNode {
    return {
        type: 'bulletList',
        content: items.map((item) => ({ type: 'listItem', content: [paragraph([item])] }))
    };
}

export function docOf(nodes: BlockNode[]): TiptapDoc {
    return { type: 'doc', content: nodes };
}

/** Paragraphs separated by exactly one blank line each. */
export function toDoc(blocks: Block[]): TiptapDoc {
    return docOf(blocks.flatMap((block, i) => [...(i > 0 ? [blankLine()] : []), paragraph(block)]));
}
